import React, { useState, useEffect, useContext, useRef } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowLeft, Building2, Plus, Edit, Trash2, Save, X,
  Upload, Image, Palette, Check, Globe, RefreshCw,
  Download, FileText, AlertTriangle, Undo2
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { AuthContext } from "../App";
import { toast } from "sonner";
import { DEFAULT_EMPRESA_MODULOS, EMPRESA_MODULOS, EMPRESA_MODULOS_OBLIGATORIOS, modulosHabilitadosEmpresa, validarDependenciasModulos } from "../lib/modulosEmpresa";
import SucursalesPanel from "../components/SucursalesPanel";
import RetencionAgentePanel from "../components/RetencionAgentePanel";
import { resolveLogoForContext, logoSizePx } from "../lib/logoUtils";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const readApiError = async (res, fallback) => {
  try {
    const err = await res.json();
    const detail = err.detail;
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail)) return detail.map((item) => item.msg || JSON.stringify(item)).join(" · ");
    if (detail) return String(detail);
  } catch (_) {
    // The backend may return an empty or non-JSON error body.
  }
  return `${fallback} (${res.status})`;
};

// ── Catálogo de temas disponibles ─────────────────────────────────────────────
const TEMAS = [
  {
    id: "oscuro-azul",
    label: "Oscuro Azul",
    desc: "Tema por defecto. Fondo oscuro con acento azul.",
    preview: { bg: "#0a1628", card: "#0f1e37", accent: "#3b82f6", text: "#f1f5f9" },
  },
  {
    id: "oscuro-rojo",
    label: "Oscuro Rojo",
    desc: "Fondo oscuro con acento rojo. Ideal para empresas de seguridad.",
    preview: { bg: "#0a1628", card: "#0f1e37", accent: "#ef4444", text: "#f1f5f9" },
  },
  {
    id: "claro-rojo",
    label: "Claro Rojo",
    desc: "Fondo blanco/gris claro con acento rojo corporativo.",
    preview: { bg: "#f8fafc", card: "#ffffff", accent: "#dc2626", text: "#1e293b" },
  },
  {
    id: "claro-dorado",
    label: "Claro Dorado",
    desc: "Fondo claro amarillo cálido con acento dorado. Look premium.",
    preview: { bg: "#fefce8", card: "#ffffff", accent: "#d97706", text: "#1c1917" },
  },
  {
    id: "claro-azul",
    label: "Claro Azul",
    desc: "Fondo blanco/azul claro con acento azul. Look moderno y profesional.",
    preview: { bg: "#f0f9ff", card: "#ffffff", accent: "#2563eb", text: "#0f172a" },
  },
];

// Mini preview de tema
const TemaPreview = ({ tema, selected, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className={`relative p-0.5 rounded-xl transition-all ${
      selected ? "ring-2 ring-offset-2 ring-offset-arandu-dark-light" : "hover:scale-105"
    }`}
    style={{ ringColor: tema.preview.accent }}
    title={tema.label}
  >
    {selected && (
      <div className="absolute -top-1.5 -right-1.5 z-10 w-5 h-5 rounded-full flex items-center justify-center"
        style={{ backgroundColor: tema.preview.accent }}>
        <Check className="w-3 h-3 text-white" />
      </div>
    )}
    {/* Mini UI mockup */}
    <div className="w-28 h-20 rounded-lg overflow-hidden border border-white/10"
      style={{ backgroundColor: tema.preview.bg }}>
      {/* Sidebar strip */}
      <div className="flex h-full">
        <div className="w-6 flex flex-col gap-1 p-1" style={{ backgroundColor: tema.preview.card }}>
          {[...Array(4)].map((_, i) => (
            <div key={i} className="w-4 h-1.5 rounded-sm"
              style={{ backgroundColor: i === 0 ? tema.preview.accent : "rgba(128,128,128,0.3)" }} />
          ))}
        </div>
        <div className="flex-1 p-1.5 space-y-1">
          <div className="h-2 rounded" style={{ backgroundColor: tema.preview.card }} />
          <div className="h-2 rounded w-3/4" style={{ backgroundColor: tema.preview.card }} />
          <div className="h-4 rounded mt-1 flex items-center justify-center"
            style={{ backgroundColor: `${tema.preview.accent}25` }}>
            <div className="w-8 h-1.5 rounded" style={{ backgroundColor: tema.preview.accent }} />
          </div>
          <div className="h-2 rounded w-1/2" style={{ backgroundColor: tema.preview.card }} />
        </div>
      </div>
    </div>
    <p className="text-center text-xs mt-1.5"
      style={{ color: selected ? tema.preview.accent : "#94a3b8" }}>
      {tema.label}
    </p>
  </button>
);

// ── Constantes para gestión de logos ─────────────────────────────────────────
const SIZE_OPTIONS = [
  { key: "xs", label: "XS", px: 32 },
  { key: "s",  label: "S",  px: 44 },
  { key: "m",  label: "M",  px: 56 },
  { key: "l",  label: "L",  px: 72 },
  { key: "xl", label: "XL", px: 88 },
];
const ETIQUETA_OPTS = [
  { value: "general", label: "General" },
  { value: "oscuro",  label: "Oscuro"  },
  { value: "claro",   label: "Claro"   },
];

// ── ContextConfig: configuración por contexto (panel / docs) ──────────────────
const ContextConfig = ({ title, icon, mode, setMode, selectedId, setSelectedId, size, setSize, logos, preferEtiqueta }) => {
  const previewUrl = (() => {
    if (mode === "manual" && selectedId) return logos.find(l => l.id === selectedId)?.url || null;
    if (logos.length === 0) return null;
    return logos.find(l => l.etiqueta === preferEtiqueta)?.url
      || logos.find(l => l.etiqueta === "general")?.url
      || logos[0]?.url
      || null;
  })();
  const previewPx = logoSizePx(size);

  return (
    <div className="bg-arandu-dark/60 border border-white/10 rounded-xl p-4">
      <div className="flex items-center gap-2 mb-3">
        <span className="text-slate-400">{icon}</span>
        <h4 className="text-slate-200 text-sm font-semibold">{title}</h4>
      </div>

      {/* Mode toggle */}
      <div className="flex gap-2 mb-4">
        {["auto", "manual"].map(m => (
          <button key={m} type="button"
            onClick={() => setMode(m)}
            className={`flex-1 py-1.5 rounded-lg text-xs font-medium transition-all ${
              mode === m
                ? "bg-arandu-blue text-white"
                : "bg-arandu-dark text-slate-400 hover:text-slate-200 border border-white/10"
            }`}
          >
            {m === "auto" ? "Auto (por etiqueta)" : "Manual (elegir logo)"}
          </button>
        ))}
      </div>

      {/* Auto: info */}
      {mode === "auto" && (
        <p className="text-slate-500 text-xs mb-4">
          Buscará logo con etiqueta <span className="text-slate-300 font-mono">"{preferEtiqueta}"</span>, luego "general", luego el primero disponible.
        </p>
      )}

      {/* Manual: logo picker */}
      {mode === "manual" && (
        <div className="mb-4">
          {logos.length === 0 ? (
            <p className="text-slate-500 text-xs">Primero subí logos a la librería de arriba.</p>
          ) : (
            <div className="flex flex-wrap gap-2">
              {logos.map(l => (
                <button key={l.id} type="button"
                  onClick={() => setSelectedId(l.id)}
                  title={l.nombre || l.etiqueta}
                  className={`rounded-xl border p-1.5 transition-all ${
                    selectedId === l.id
                      ? "border-arandu-blue ring-1 ring-arandu-blue"
                      : "border-white/10 hover:border-white/30"
                  }`}
                >
                  <img src={l.url} alt={l.nombre} className="w-12 h-12 object-contain rounded" />
                  <p className="text-[10px] text-slate-500 mt-1 text-center w-12 truncate">{l.etiqueta}</p>
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Size selector */}
      <div className="mb-3">
        <p className="text-slate-400 text-xs mb-2">Tamaño:</p>
        <div className="flex gap-1.5">
          {SIZE_OPTIONS.map(s => (
            <button key={s.key} type="button"
              onClick={() => setSize(s.key)}
              className={`w-9 py-1 rounded text-xs font-medium transition-all ${
                size === s.key
                  ? "bg-arandu-blue text-white"
                  : "bg-arandu-dark text-slate-400 border border-white/10 hover:border-white/30"
              }`}
            >{s.label}</button>
          ))}
        </div>
      </div>

      {/* Preview */}
      <div className="flex items-center gap-3 p-3 bg-arandu-dark rounded-lg border border-white/5">
        <div className="flex-shrink-0 flex items-center justify-center rounded-lg overflow-hidden"
          style={{ width: previewPx, height: previewPx }}>
          {previewUrl ? (
            <img src={previewUrl} alt="preview"
              style={{ maxWidth: previewPx, maxHeight: previewPx, objectFit: "contain" }} />
          ) : (
            <div className="w-full h-full rounded-lg bg-slate-700/40 flex items-center justify-center">
              <Image className="w-5 h-5 text-slate-600" />
            </div>
          )}
        </div>
        <div>
          <p className="text-slate-400 text-xs">{previewUrl ? "Vista previa del logo" : "Sin logo (usará marca de texto)"}</p>
          <p className="text-slate-600 text-[11px]">{previewPx}px de alto</p>
        </div>
      </div>
    </div>
  );
};

// ── LogoManagerModal ──────────────────────────────────────────────────────────
const LogoManagerModal = ({ empresa, token, onClose, onSaved }) => {
  const [logos, setLogos] = useState(empresa.logos || []);
  const [panelMode, setPanelMode] = useState(empresa.logo_panel_mode || "auto");
  const [panelLogoId, setPanelLogoId] = useState(empresa.logo_panel_id || null);
  const [panelSize, setPanelSize] = useState(empresa.logo_panel_size || "m");
  const [docsMode, setDocsMode] = useState(empresa.logo_docs_mode || "auto");
  const [docsLogoId, setDocsLogoId] = useState(empresa.logo_docs_id || null);
  const [docsSize, setDocsSize] = useState(empresa.logo_docs_size || "m");
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingLogo, setEditingLogo] = useState(null); // { id, nombre, etiqueta }
  const fileRef = useRef(null);

  const handleUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) { toast.error("Máximo 2 MB"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("nombre", file.name.replace(/\.[^.]+$/, ""));
      fd.append("etiqueta", "general");
      const res = await fetch(`${API}/admin/empresas-propias/${empresa.id}/logos`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });
      if (res.ok) { const data = await res.json(); setLogos(data.logos || []); onSaved(); toast.success("Logo subido"); }
      else toast.error(await readApiError(res, "Error al subir logo"), { duration: 8000 });
    } catch (error) {
      console.error("Error al subir logo:", error);
      toast.error("Error de conexión al subir logo. Revisá que el backend esté activo.");
    }
    finally { setUploading(false); e.target.value = ""; }
  };

  const handleDeleteLogo = async (logoId) => {
    if (!window.confirm("¿Eliminar este logo de la librería?")) return;
    try {
      const res = await fetch(`${API}/admin/empresas-propias/${empresa.id}/logos/${logoId}`, {
        method: "DELETE", headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setLogos(data.logos || []);
        if (panelLogoId === logoId) setPanelLogoId(null);
        if (docsLogoId === logoId) setDocsLogoId(null);
        onSaved();
        toast.success("Logo eliminado");
      } else toast.error(await readApiError(res, "Error al eliminar logo"), { duration: 8000 });
    } catch (error) {
      console.error("Error al eliminar logo:", error);
      toast.error("Error de conexión al eliminar logo");
    }
  };

  const handleSaveLogoEdit = async () => {
    if (!editingLogo) return;
    try {
      const res = await fetch(`${API}/admin/empresas-propias/${empresa.id}/logos/${editingLogo.id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ nombre: editingLogo.nombre, etiqueta: editingLogo.etiqueta }),
      });
      if (res.ok) { const data = await res.json(); setLogos(data.logos || []); setEditingLogo(null); onSaved(); toast.success("Actualizado"); }
      else toast.error(await readApiError(res, "Error al actualizar logo"), { duration: 8000 });
    } catch (error) {
      console.error("Error al actualizar logo:", error);
      toast.error("Error de conexión al actualizar logo");
    }
  };

  const handleDownload = (logo) => {
    const a = document.createElement("a");
    a.href = logo.url;
    a.download = `${logo.nombre || "logo"}.png`;
    a.click();
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API}/admin/empresas-propias/${empresa.id}/logo-config`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          logo_panel_mode: panelMode,
          logo_panel_id: panelLogoId || null,
          logo_panel_size: panelSize,
          logo_docs_mode: docsMode,
          logo_docs_id: docsLogoId || null,
          logo_docs_size: docsSize,
        }),
      });
      if (res.ok) { toast.success("Configuración guardada"); onSaved(); onClose(); }
      else toast.error(await readApiError(res, "Error al guardar configuración"), { duration: 8000 });
    } catch (error) {
      console.error("Error al guardar configuración de logos:", error);
      toast.error("Error de conexión al guardar configuración");
    }
    finally { setSaving(false); }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/80 z-50 flex items-start justify-center p-4 overflow-y-auto"
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.9, opacity: 0 }}
        className="bg-arandu-dark-light border border-white/10 rounded-2xl w-full max-w-2xl p-6 my-8"
      >
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <Image className="w-5 h-5 text-arandu-blue" />
            <div>
              <h2 className="font-heading text-xl font-bold text-white">Gestionar logos</h2>
              <p className="text-slate-400 text-xs mt-0.5">{empresa.nombre}</p>
            </div>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-white"><X className="w-6 h-6" /></button>
        </div>

        {/* ── Librería de logos ── */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-slate-300 text-sm font-semibold">Librería de logos</h3>
            <div>
              <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={handleUpload} />
              <Button type="button" size="sm" onClick={() => fileRef.current?.click()}
                disabled={uploading}
                className="bg-arandu-blue hover:bg-arandu-blue-dark text-white text-xs">
                <Upload className="w-3.5 h-3.5 mr-1.5" />
                {uploading ? "Subiendo..." : "Subir logo"}
              </Button>
            </div>
          </div>

          {logos.length === 0 ? (
            <div className="text-center py-8 border border-dashed border-white/10 rounded-xl">
              <Image className="w-10 h-10 text-slate-700 mx-auto mb-2" />
              <p className="text-slate-500 text-sm">No hay logos en la librería</p>
              <p className="text-slate-600 text-xs mt-1">Subí imágenes para asignarlas a cada contexto</p>
            </div>
          ) : (
            <div className="space-y-2">
              {logos.map(logo => (
                <div key={logo.id}
                  className="flex items-center gap-3 bg-arandu-dark/60 border border-white/10 rounded-xl p-3">
                  {/* Thumbnail */}
                  <div className="w-14 h-14 rounded-lg border border-white/10 bg-arandu-dark flex items-center justify-center overflow-hidden flex-shrink-0">
                    <img src={logo.url} alt={logo.nombre} className="w-full h-full object-contain p-1" />
                  </div>

                  {editingLogo?.id === logo.id ? (
                    <div className="flex-1 flex items-center gap-2 flex-wrap">
                      <Input value={editingLogo.nombre}
                        onChange={e => setEditingLogo(prev => ({ ...prev, nombre: e.target.value }))}
                        className="bg-arandu-dark border-white/10 text-white text-sm h-8 flex-1 min-w-0"
                        placeholder="Nombre" />
                      <select value={editingLogo.etiqueta}
                        onChange={e => setEditingLogo(prev => ({ ...prev, etiqueta: e.target.value }))}
                        className="bg-arandu-dark border border-white/10 rounded-lg text-slate-300 text-xs px-2 h-8">
                        {ETIQUETA_OPTS.map(o => (
                          <option key={o.value} value={o.value}>{o.label}</option>
                        ))}
                      </select>
                      <button onClick={handleSaveLogoEdit} className="text-emerald-400 hover:text-emerald-300">
                        <Check className="w-4 h-4" />
                      </button>
                      <button onClick={() => setEditingLogo(null)} className="text-slate-500 hover:text-slate-300">
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ) : (
                    <>
                      <div className="flex-1 min-w-0">
                        <p className="text-slate-200 text-sm truncate">{logo.nombre || "Logo"}</p>
                        <span className={`inline-block text-[10px] rounded px-1.5 py-0.5 mt-1 font-medium ${
                          logo.etiqueta === "oscuro" ? "bg-slate-700 text-slate-300"
                          : logo.etiqueta === "claro" ? "bg-amber-500/20 text-amber-300"
                          : "bg-slate-600/40 text-slate-400"
                        }`}>{logo.etiqueta}</span>
                      </div>
                      <div className="flex gap-1 flex-shrink-0">
                        <button type="button" title="Editar etiqueta/nombre"
                          onClick={() => setEditingLogo({ id: logo.id, nombre: logo.nombre || "", etiqueta: logo.etiqueta })}
                          className="text-slate-400 hover:text-yellow-400 p-1.5 rounded hover:bg-yellow-500/10 transition-colors">
                          <Edit className="w-3.5 h-3.5" />
                        </button>
                        <button type="button" title="Descargar"
                          onClick={() => handleDownload(logo)}
                          className="text-slate-400 hover:text-arandu-blue p-1.5 rounded hover:bg-arandu-blue/10 transition-colors">
                          <Download className="w-3.5 h-3.5" />
                        </button>
                        <button type="button" title="Eliminar"
                          onClick={() => handleDeleteLogo(logo.id)}
                          className="text-slate-400 hover:text-red-400 p-1.5 rounded hover:bg-red-500/10 transition-colors">
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* ── Configuración por contexto ── */}
        <div className="space-y-4 mb-6">
          <h3 className="text-slate-300 text-sm font-semibold">Uso por contexto</h3>
          <ContextConfig
            title="Panel / Sidebar"
            icon={<Globe className="w-4 h-4" />}
            mode={panelMode} setMode={setPanelMode}
            selectedId={panelLogoId} setSelectedId={setPanelLogoId}
            size={panelSize} setSize={setPanelSize}
            logos={logos}
            preferEtiqueta="oscuro"
          />
          <ContextConfig
            title="Documentos impresos (facturas, presupuestos)"
            icon={<FileText className="w-4 h-4" />}
            mode={docsMode} setMode={setDocsMode}
            selectedId={docsLogoId} setSelectedId={setDocsLogoId}
            size={docsSize} setSize={setDocsSize}
            logos={logos}
            preferEtiqueta="claro"
          />
        </div>

        {/* Footer */}
        <div className="flex gap-3">
          <Button type="button" variant="outline" onClick={onClose}
            className="flex-1 border-white/10 text-slate-400">
            <X className="w-4 h-4 mr-2" /> Cancelar
          </Button>
          <Button type="button" disabled={saving} onClick={handleSave}
            className="flex-1 bg-arandu-blue hover:bg-arandu-blue-dark text-white">
            <Save className="w-4 h-4 mr-2" />
            {saving ? "Guardando..." : "Guardar configuración"}
          </Button>
        </div>
      </motion.div>
    </motion.div>
  );
};

// ── WipeConfirmModal: confirmación de limpieza de base de datos ───────────────
const WipeConfirmModal = ({ empresa, confirmText, setConfirmText, saving, onClose, onConfirm }) => {
  const matches = confirmText.trim() === empresa.nombre;
  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/80 z-50 flex items-start justify-center p-4 overflow-y-auto"
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.9, opacity: 0 }}
        className="bg-arandu-dark-light border border-red-500/30 rounded-2xl w-full max-w-md p-6 my-8"
      >
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-full bg-red-500/10 flex items-center justify-center flex-shrink-0">
            <AlertTriangle className="w-5 h-5 text-red-400" />
          </div>
          <div>
            <h2 className="font-heading text-lg font-bold text-white">Eliminar base de datos</h2>
            <p className="text-slate-400 text-xs mt-0.5">{empresa.nombre}</p>
          </div>
        </div>

        <p className="text-slate-300 text-sm mb-3">
          Se van a borrar <strong>todos los registros</strong> de <strong>{empresa.nombre}</strong> (ventas, productos,
          clientes, facturas, etc.) para empezar de cero. Los usuarios y el registro de auditoría no se tocan.
        </p>
        <p className="text-slate-400 text-sm mb-4">
          Vas a poder recuperar estos datos después desde este mismo panel, hasta que decidas eliminar la
          recuperación de forma definitiva.
        </p>

        <label className="text-slate-400 text-xs mb-1.5 block">
          Escribí <span className="text-white font-mono">{empresa.nombre}</span> para confirmar
        </label>
        <Input value={confirmText}
          onChange={e => setConfirmText(e.target.value)}
          className="bg-arandu-dark border-white/10 text-white mb-5"
          placeholder={empresa.nombre} autoFocus />

        <div className="flex gap-3">
          <Button type="button" variant="outline" onClick={onClose}
            className="flex-1 border-white/10 text-slate-400">
            <X className="w-4 h-4 mr-2" /> Cancelar
          </Button>
          <Button type="button" disabled={!matches || saving} onClick={onConfirm}
            className="flex-1 bg-red-600 hover:bg-red-500 text-white disabled:opacity-40 disabled:cursor-not-allowed">
            <Trash2 className="w-4 h-4 mr-2" />
            {saving ? "Limpiando..." : "Confirmar y limpiar"}
          </Button>
        </div>
      </motion.div>
    </motion.div>
  );
};

// ── Componente principal ──────────────────────────────────────────────────────
const EmpresasPropiasPage = () => {
  const { token, refreshEmpresasPropias, user, hasPermission } = useContext(AuthContext);
  const isSuperAdmin = user?.role === "admin";
  // Nombre real: "isSuperAdmin" de arriba en realidad solo verifica role==="admin"
  // "admin" ES el rol tope real del sistema (la propia UI de Usuarios llama a ese
  // rol "Super Admin" al crearlo). El string literal "super_admin" nunca se asigna
  // a ningún usuario real, así que gatear con eso dejaba el botón inalcanzable.
  // Regla confirmada con el dueño del sistema (sep-2026): ver nota en auth.py.
  const isSuperAdminReal = user?.role === "admin";
  const [empresas, setEmpresas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const fileInputRef = useRef(null);
  const [showLogoManager, setShowLogoManager] = useState(false);
  const [logoManagerEmpresa, setLogoManagerEmpresa] = useState(null);
  const contratoFileRef = useRef(null);
  const [contratoMenuId, setContratoMenuId] = useState(null); // id empresa con menú abierto
  const [uploadingContrato, setUploadingContrato] = useState(null); // id empresa uploading

  // ── Limpieza de base de datos por empresa ──────────────────────────────────
  const [wipesActivos, setWipesActivos] = useState({}); // empresa_id -> wipe doc
  const [showWipeModal, setShowWipeModal] = useState(false);
  const [wipeTargetEmpresa, setWipeTargetEmpresa] = useState(null);
  const [wipeConfirmText, setWipeConfirmText] = useState("");
  const [wipeSaving, setWipeSaving] = useState(false);
  const [restaurando, setRestaurando] = useState(null); // wipe id restaurando
  const [purgando, setPurgando] = useState(null); // wipe id purgando

  const emptyForm = () => ({
    nombre: "",
    slug: "",
    razon_social: "",
    nombre_fantasia: "",
    ruc: "",
    direccion: "",
    telefono: "",
    email: "",
    nro_patronal_ips: "",
    nro_patronal_mtess: "",
    rubros_dinet: "",
    color: "#3b82f6",
    tema: "oscuro-azul",
    modulos_habilitados: [...DEFAULT_EMPRESA_MODULOS],
    monedas_habilitadas: ["PYG"],
  });
  const [form, setForm] = useState(emptyForm());
  const [logoPreview, setLogoPreview] = useState(null); // base64 preview antes de subir

  useEffect(() => { fetchEmpresas(); if (isSuperAdminReal) fetchWipesActivos(); }, []);

  const fetchWipesActivos = async () => {
    try {
      const res = await fetch(`${API}/admin/empresas-propias/wipes/activos`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) {
        const data = await res.json();
        const map = {};
        (data || []).forEach(w => { map[w.empresa_id] = w; });
        setWipesActivos(map);
      }
    } catch (e) { console.error("Error al cargar respaldos de limpieza:", e); }
  };

  const openWipeModal = (ep) => {
    setWipeTargetEmpresa(ep);
    setWipeConfirmText("");
    setShowWipeModal(true);
  };

  const handleWipeConfirm = async () => {
    if (!wipeTargetEmpresa) return;
    if (wipeConfirmText.trim() !== wipeTargetEmpresa.nombre) {
      toast.error("El nombre no coincide");
      return;
    }
    setWipeSaving(true);
    try {
      const res = await fetch(`${API}/admin/empresas-propias/${wipeTargetEmpresa.id}/wipe`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ confirmar_nombre: wipeConfirmText.trim() }),
      });
      if (res.ok) {
        const data = await res.json();
        toast.success(`Base de datos de ${wipeTargetEmpresa.nombre} limpiada. Podés recuperarla desde este mismo panel.`);
        setWipesActivos(prev => ({ ...prev, [wipeTargetEmpresa.id]: data }));
        setShowWipeModal(false);
        setWipeTargetEmpresa(null);
        setWipeConfirmText("");
      } else {
        toast.error(await readApiError(res, "Error al limpiar la base de datos"), { duration: 8000 });
      }
    } catch (e) {
      console.error("Error al limpiar base de datos:", e);
      toast.error("Error de conexión al limpiar la base de datos");
    } finally {
      setWipeSaving(false);
    }
  };

  const handleRestaurarWipe = async (wipe) => {
    if (!window.confirm(`¿Restaurar todos los datos de ${wipe.empresa_nombre}? Se van a reinsertar ${wipe.total_documentos} registros y se va a eliminar este respaldo.`)) return;
    setRestaurando(wipe.id);
    try {
      const res = await fetch(`${API}/admin/empresas-propias/wipe/${wipe.id}/restaurar`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        toast.success("Datos restaurados");
        setWipesActivos(prev => { const next = { ...prev }; delete next[wipe.empresa_id]; return next; });
        fetchEmpresas();
      } else {
        toast.error(await readApiError(res, "Error al restaurar"), { duration: 8000 });
      }
    } catch (e) {
      console.error("Error al restaurar wipe:", e);
      toast.error("Error de conexión al restaurar");
    } finally {
      setRestaurando(null);
    }
  };

  const handlePurgarWipe = async (wipe) => {
    if (!window.confirm(`¿Eliminar definitivamente la recuperación de ${wipe.empresa_nombre}? A partir de acá los ${wipe.total_documentos} registros NO se van a poder recuperar nunca más.`)) return;
    setPurgando(wipe.id);
    try {
      const res = await fetch(`${API}/admin/empresas-propias/wipe/${wipe.id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        toast.success("Recuperación eliminada");
        setWipesActivos(prev => { const next = { ...prev }; delete next[wipe.empresa_id]; return next; });
      } else {
        toast.error(await readApiError(res, "Error al eliminar recuperación"), { duration: 8000 });
      }
    } catch (e) {
      console.error("Error al purgar wipe:", e);
      toast.error("Error de conexión al eliminar recuperación");
    } finally {
      setPurgando(null);
    }
  };

  const fetchEmpresas = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/admin/empresas-propias`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) {
        const data = await res.json();
        if (!isSuperAdmin) {
          const asignados = user?.logos_asignados || [];
          setEmpresas(data.filter(e => asignados.includes(e.id)));
        } else {
          setEmpresas(data);
        }
      }
    } catch (e) { toast.error("Error al cargar empresas"); }
    finally { setLoading(false); }
  };

  // ── Handlers contrato ─────────────────────────────────────────────────────
  const handleUploadContrato = async (empresaId, file) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error("Máximo 5 MB"); return; }
    setUploadingContrato(empresaId);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await fetch(`${API}/admin/empresas-propias/${empresaId}/contrato`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });
      if (res.ok) { toast.success("Contrato cargado"); fetchEmpresas(); }
      else { const e = await readApiError(res, "Error al subir contrato"); toast.error(e); }
    } catch { toast.error("Error de conexión"); }
    finally { setUploadingContrato(null); contratoFileRef.current && (contratoFileRef.current.value = ""); }
  };

  const handleDownloadContrato = async (ep) => {
    setContratoMenuId(null);
    try {
      const res = await fetch(`${API}/admin/empresas-propias/${ep.id}/contrato`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) { toast.error("No se pudo descargar el contrato"); return; }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = ep.contrato_meta?.nombre || "contrato.pdf";
      a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Error al descargar"); }
  };

  const handleDeleteContrato = async (ep) => {
    if (!window.confirm(`¿Eliminar el contrato de ${ep.nombre}?`)) return;
    setContratoMenuId(null);
    try {
      const res = await fetch(`${API}/admin/empresas-propias/${ep.id}/contrato`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) { toast.success("Contrato eliminado"); fetchEmpresas(); }
      else toast.error("Error al eliminar contrato");
    } catch { toast.error("Error de conexión"); }
  };

  const openNew = () => {
    setForm(emptyForm());
    setLogoPreview(null);
    setEditingId(null);
    setShowForm(true);
  };

  const openEdit = (ep) => {
    setForm({
      nombre: ep.nombre || "",
      slug: ep.slug || "",
      razon_social: ep.razon_social || "",
      nombre_fantasia: ep.nombre_fantasia || "",
      ruc: ep.ruc || "",
      direccion: ep.direccion || "",
      telefono: ep.telefono || "",
      email: ep.email || "",
      nro_patronal_ips: ep.nro_patronal_ips || "",
      nro_patronal_mtess: ep.nro_patronal_mtess || "",
      rubros_dinet: ep.rubros_dinet || "",
      color: ep.color || "#3b82f6",
      tema: ep.tema || "oscuro-azul",
      modulos_habilitados: modulosHabilitadosEmpresa(ep),
      monedas_habilitadas: ep.monedas_habilitadas || ["PYG"],
    });
    setLogoPreview(ep.logo_url || null);
    setEditingId(ep.id);
    setShowForm(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.nombre.trim()) { toast.error("El nombre es requerido"); return; }
    const modulosAGuardar = [...new Set([...EMPRESA_MODULOS_OBLIGATORIOS, ...(form.modulos_habilitados || [])])];
    const problemasDependencias = validarDependenciasModulos(modulosAGuardar);
    if (problemasDependencias.length > 0) {
      problemasDependencias.forEach(p => {
        toast.error(`El módulo "${p.moduloLabel}" necesita que también esté habilitado: ${p.faltantesLabels.join(", ")}`);
      });
      return;
    }
    setSaving(true);
    try {
      const url = editingId ? `${API}/admin/empresas-propias/${editingId}` : `${API}/admin/empresas-propias`;
      const res = await fetch(url, {
        method: editingId ? "PUT" : "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          ...form,
          modulos_habilitados: modulosAGuardar,
          monedas_habilitadas: form.monedas_habilitadas || ["PYG"],
        }),
      });
      if (res.ok) {
        const saved = await res.json();
        // Si hay un logo nuevo seleccionado (base64 aún no subido), subirlo
        if (logoPreview && logoPreview !== saved.logo_url && logoPreview.startsWith("data:")) {
          await uploadLogoFromBase64(saved.id, logoPreview);
        }
        toast.success(editingId ? "Empresa actualizada" : "Empresa creada");
        setShowForm(false);
        await fetchEmpresas();
        refreshEmpresasPropias();
      } else {
        let msg = "Error al guardar";
        try {
          const err = await res.json();
          const d = err.detail;
          if (typeof d === "string") msg = d;
          else if (Array.isArray(d)) msg = d.map((x) => x.msg || JSON.stringify(x)).join(" · ");
          else if (d) msg = String(d);
          else msg = `${msg} (${res.status})`;
        } catch {
          msg = `Error ${res.status}`;
        }
        toast.error(msg);
      }
    } catch (ex) {
      console.error(ex);
      toast.error("Error de conexión");
    }
    finally { setSaving(false); }
  };

  const uploadLogoFromBase64 = async (id, dataUrl) => {
    // Convertir base64 data URL a File y subir via /logo endpoint
    try {
      const arr = dataUrl.split(",");
      const mime = arr[0].match(/:(.*?);/)[1];
      const bstr = atob(arr[1]);
      const u8arr = new Uint8Array(bstr.length);
      for (let i = 0; i < bstr.length; i++) u8arr[i] = bstr.charCodeAt(i);
      const blob = new Blob([u8arr], { type: mime });
      const fd = new FormData();
      fd.append("file", blob, "logo.png");
      await fetch(`${API}/admin/empresas-propias/${id}/logo`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });
    } catch (e) { console.error("Error subiendo logo:", e); }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) { toast.error("La imagen debe pesar menos de 2 MB"); return; }
    const reader = new FileReader();
    reader.onload = (ev) => setLogoPreview(ev.target.result);
    reader.readAsDataURL(file);
  };

  const handleDelete = async (id, nombre) => {
    if (!window.confirm(`¿Eliminar la empresa "${nombre}"? Esta acción no se puede deshacer.`)) return;
    try {
      const res = await fetch(`${API}/admin/empresas-propias/${id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) { toast.success("Empresa eliminada"); fetchEmpresas(); refreshEmpresasPropias(); }
      else toast.error("No se pudo eliminar");
    } catch { toast.error("Error"); }
  };

  const temaInfo = (slug) => TEMAS.find(t => t.id === slug) || TEMAS[0];
  const toggleModulo = (modulo) => {
    if (EMPRESA_MODULOS_OBLIGATORIOS.includes(modulo)) return;
    setForm(prev => {
      const current = prev.modulos_habilitados || [];
      return {
        ...prev,
        modulos_habilitados: current.includes(modulo)
          ? current.filter(m => m !== modulo)
          : [...current, modulo],
      };
    });
  };

  return (
    <div className="min-h-screen bg-arandu-dark p-4 md:p-8">
      <div className="max-w-5xl mx-auto">

        {/* Header */}
        <div className="mb-8">
          <Link to="/sistema" className="text-slate-400 hover:text-arandu-blue flex items-center gap-2 mb-3 text-sm w-fit">
            <ArrowLeft className="w-4 h-4" /> Volver al Dashboard
          </Link>
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <Building2 className="w-8 h-8 text-arandu-blue" />
              <div>
                <h1 className="font-heading text-3xl font-bold text-white">Empresas Propias</h1>
                <p className="text-slate-400 text-sm">
                  {isSuperAdmin
                    ? "Configurá tus empresas con logo, tema de color y acceso multi-empresa"
                    : "Las empresas que administrás"}
                </p>
              </div>
            </div>
            {isSuperAdmin && (
              <Button onClick={openNew} className="bg-arandu-blue hover:bg-arandu-blue-dark text-white">
                <Plus className="w-4 h-4 mr-2" /> Nueva Empresa
              </Button>
            )}
          </div>
        </div>

        {/* Info banner */}
        <div className="bg-arandu-blue/10 border border-arandu-blue/20 rounded-xl p-4 mb-6 text-sm text-slate-300">
          <p className="font-medium text-arandu-blue mb-1">¿Cómo funciona el sistema multi-empresa?</p>
          <p>Cada empresa tiene su propio <strong>slug</strong> (identificador único) que se asigna a usuarios y documenntos.
          El <strong>logo</strong> aparece en el panel y en los presupuestos. El <strong>tema</strong> se aplica automáticamente
          cuando un usuario de esa empresa inicia sesión.</p>
        </div>

        {isSuperAdminReal && <SucursalesPanel token={token} empresas={empresas} />}

        {/* Lista */}
        {loading ? (
          <div className="text-center py-16 text-arandu-blue animate-pulse">Cargando...</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {empresas.map(ep => {
              const tema = temaInfo(ep.tema);
              return (
                <motion.div key={ep.id}
                  initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
                  className="bg-arandu-dark-light border border-white/5 rounded-xl overflow-hidden hover:border-arandu-blue/20 transition-all"
                >
                  {/* Color strip */}
                  <div className="h-1.5" style={{ backgroundColor: ep.color || "#3b82f6" }} />
                  <div className="p-5">
                    <div className="flex items-start gap-4">
                      {/* Logo */}
                      {(() => {
                        const panelLogoUrl = resolveLogoForContext(ep, "panel");
                        return (
                          <div className="w-16 h-16 rounded-xl border border-white/10 flex items-center justify-center flex-shrink-0 overflow-hidden"
                            style={{ backgroundColor: `${ep.color || "#3b82f6"}18` }}>
                            {panelLogoUrl ? (
                              <img src={panelLogoUrl} alt={ep.nombre} className="w-full h-full object-contain p-1" />
                            ) : (
                              <Building2 className="w-8 h-8" style={{ color: ep.color || "#3b82f6" }} />
                            )}
                          </div>
                        );
                      })()}

                      {/* Info */}
                      <div className="flex-1 min-w-0">
                        <h3 className="text-white font-semibold text-lg">{ep.nombre}</h3>
                        <p className="text-slate-500 text-xs font-mono mt-0.5">slug: <span className="text-slate-300">{ep.slug}</span></p>
                        {(ep.razon_social || ep.ruc) && (
                          <p className="text-slate-400 text-xs mt-1 truncate">
                            {ep.razon_social || ep.nombre}{ep.ruc ? ` · RUC ${ep.ruc}` : ""}
                          </p>
                        )}
                        <div className="flex items-center gap-2 mt-2">
                          <div className="flex items-center gap-1.5 bg-arandu-dark rounded-lg px-2.5 py-1">
                            <div className="w-3 h-3 rounded-full" style={{ backgroundColor: tema.preview.accent }} />
                            <span className="text-xs text-slate-400">{tema.label}</span>
                          </div>
                          <div className="flex items-center gap-1.5 bg-arandu-dark rounded-lg px-2.5 py-1">
                            <Check className="w-3 h-3 text-emerald-400" />
                            <span className="text-xs text-slate-400">{modulosHabilitadosEmpresa(ep).length} módulos</span>
                          </div>
                        </div>
                      </div>

                      {/* Acciones */}
                      <div className="flex gap-1 flex-shrink-0 items-center">
                        <Button onClick={() => { setLogoManagerEmpresa(ep); setShowLogoManager(true); }}
                          variant="ghost" title="Gestionar logos"
                          className="text-slate-400 hover:text-arandu-blue hover:bg-arandu-blue/10">
                          <Image className="w-4 h-4" />
                        </Button>

                        {/* ── Botón contrato ── */}
                        <div className="relative">
                          {ep.contrato_meta ? (
                            <>
                              <Button
                                variant="ghost"
                                title={`Contrato: ${ep.contrato_meta.nombre}`}
                                onClick={() => setContratoMenuId(prev => prev === ep.id ? null : ep.id)}
                                className="text-teal-400 hover:text-teal-300 hover:bg-teal-500/10">
                                {uploadingContrato === ep.id
                                  ? <RefreshCw className="w-4 h-4 animate-spin" />
                                  : <FileText className="w-4 h-4" />}
                              </Button>
                              {contratoMenuId === ep.id && (
                                <div className="absolute right-0 top-full mt-1 w-44 bg-arandu-dark-light border border-white/10 rounded-xl shadow-xl z-20 py-1"
                                  onClick={e => e.stopPropagation()}>
                                  <button
                                    onClick={() => handleDownloadContrato(ep)}
                                    className="w-full flex items-center gap-2 px-3 py-2 text-sm text-slate-300 hover:bg-white/5">
                                    <Download className="w-4 h-4 text-teal-400" /> Descargar
                                  </button>
                                  <button
                                    onClick={() => { setContratoMenuId(null); contratoFileRef.current?.click(); contratoFileRef.current?.setAttribute("data-empresa-id", ep.id); }}
                                    className="w-full flex items-center gap-2 px-3 py-2 text-sm text-slate-300 hover:bg-white/5">
                                    <Upload className="w-4 h-4 text-arandu-blue" /> Reemplazar
                                  </button>
                                  <button
                                    onClick={() => handleDeleteContrato(ep)}
                                    className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-400 hover:bg-red-500/10">
                                    <Trash2 className="w-4 h-4" /> Eliminar
                                  </button>
                                </div>
                              )}
                            </>
                          ) : (
                            <Button
                              variant="ghost"
                              title="Subir contrato de servicio"
                              disabled={uploadingContrato === ep.id}
                              onClick={() => { contratoFileRef.current?.setAttribute("data-empresa-id", ep.id); contratoFileRef.current?.click(); }}
                              className="text-slate-400 hover:text-teal-400 hover:bg-teal-500/10">
                              {uploadingContrato === ep.id
                                ? <RefreshCw className="w-4 h-4 animate-spin" />
                                : <FileText className="w-4 h-4" />}
                            </Button>
                          )}
                        </div>

                        <Button onClick={() => openEdit(ep)} variant="ghost"
                          className="text-slate-400 hover:text-yellow-400 hover:bg-yellow-500/10">
                          <Edit className="w-4 h-4" />
                        </Button>
                        {isSuperAdmin && (
                          <Button onClick={() => handleDelete(ep.id, ep.nombre)} variant="ghost"
                            className="text-slate-400 hover:text-red-400 hover:bg-red-500/10">
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </div>

                    {/* Limpieza de base de datos (solo admin, rol tope real) */}
                    {isSuperAdminReal && (
                      <div className="mt-4 pt-4 border-t border-white/5">
                        {wipesActivos[ep.id] ? (
                          <div className="bg-amber-500/10 border border-amber-500/20 rounded-xl p-3">
                            <div className="flex items-start gap-2 mb-2">
                              <AlertTriangle className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" />
                              <p className="text-amber-300 text-xs">
                                Base de datos limpiada el {new Date(wipesActivos[ep.id].fecha).toLocaleString("es-PY")}.
                                Hay {wipesActivos[ep.id].total_documentos} registros disponibles para recuperar.
                              </p>
                            </div>
                            <div className="flex gap-2">
                              <Button type="button" size="sm"
                                disabled={restaurando === wipesActivos[ep.id].id}
                                onClick={() => handleRestaurarWipe(wipesActivos[ep.id])}
                                className="flex-1 bg-emerald-600 hover:bg-emerald-500 text-white text-xs">
                                <Undo2 className="w-3.5 h-3.5 mr-1.5" />
                                {restaurando === wipesActivos[ep.id].id ? "Restaurando..." : "Restaurar"}
                              </Button>
                              <Button type="button" size="sm" variant="outline"
                                disabled={purgando === wipesActivos[ep.id].id}
                                onClick={() => handlePurgarWipe(wipesActivos[ep.id])}
                                className="flex-1 border-red-500/30 text-red-400 hover:bg-red-500/10 text-xs">
                                <Trash2 className="w-3.5 h-3.5 mr-1.5" />
                                {purgando === wipesActivos[ep.id].id ? "Eliminando..." : "Eliminar recuperación"}
                              </Button>
                            </div>
                          </div>
                        ) : (
                          <Button type="button" onClick={() => openWipeModal(ep)}
                            variant="outline"
                            className="w-full border-red-500/30 text-red-400 hover:bg-red-500/10 hover:border-red-500/50 text-xs">
                            <AlertTriangle className="w-3.5 h-3.5 mr-1.5" />
                            Eliminar base de datos
                          </Button>
                        )}
                      </div>
                    )}
                  </div>
                </motion.div>
              );
            })}
            {empresas.length === 0 && (
              <div className="col-span-2 text-center py-16 bg-arandu-dark-light border border-white/5 rounded-xl">
                <Building2 className="w-14 h-14 text-slate-700 mx-auto mb-3" />
                <p className="text-slate-400 mb-4">
                  {isSuperAdmin ? "No hay empresas propias creadas" : "No tenés empresas asignadas"}
                </p>
                {isSuperAdmin && (
                  <Button onClick={openNew} className="bg-arandu-blue hover:bg-arandu-blue-dark text-white">
                    <Plus className="w-4 h-4 mr-2" /> Crear primera empresa
                  </Button>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* ══ MODAL: Formulario ════════════════════════════════════════════════════ */}
      <AnimatePresence>
        {showForm && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/80 z-50 flex items-start justify-center p-4 overflow-y-auto"
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.9, opacity: 0 }}
              className="bg-arandu-dark-light border border-white/10 rounded-2xl w-full max-w-2xl p-6 my-8"
            >
              <div className="flex items-center justify-between mb-6">
                <h2 className="font-heading text-xl font-bold text-white">
                  {editingId ? "Editar Empresa" : "Nueva Empresa"}
                </h2>
                <button onClick={() => setShowForm(false)} className="text-slate-400 hover:text-white">
                  <X className="w-6 h-6" />
                </button>
              </div>

              <form onSubmit={handleSave} className="space-y-6">

                {/* Nombre + slug */}
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-slate-400 text-sm mb-1 block">Nombre *</label>
                    <Input value={form.nombre}
                      onChange={e => setForm(p => ({
                        ...p,
                        nombre: e.target.value,
                        slug: !editingId ? e.target.value.toLowerCase().replace(/\s+/g, "-").replace(/[^a-z0-9-]/g, "") : p.slug
                      }))}
                      className="bg-arandu-dark border-white/10 text-white"
                      placeholder="Ej: Mi Empresa S.A." required />
                  </div>
                  <div>
                    <label className="text-slate-400 text-sm mb-1 block">Slug (identificador)</label>
                    <Input value={form.slug}
                      onChange={e => setForm(p => ({ ...p, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "") }))}
                      className="bg-arandu-dark border-white/10 text-white font-mono"
                      placeholder="mi-empresa" />
                    <p className="text-slate-400 text-xs mt-1">Solo letras minúsculas, números y guiones. Se auto-genera del nombre.</p>
                  </div>
                </div>

                {/* Datos fiscales / facturación */}
                <div className="bg-arandu-dark/60 border border-white/10 rounded-xl p-4 space-y-4">
                  <div>
                    <h3 className="text-slate-200 text-sm font-semibold">Datos fiscales para facturación</h3>
                    <p className="text-slate-500 text-xs mt-0.5">Estos datos se usan como emisor en facturas, boletas y comprobantes.</p>
                  </div>
                  <div>
                    <label className="text-slate-400 text-sm mb-1 block">Nombre Fantasía</label>
                    <Input value={form.nombre_fantasia}
                      onChange={e => setForm(p => ({ ...p, nombre_fantasia: e.target.value }))}
                      className="bg-arandu-dark border-white/10 text-white"
                      placeholder="Ej: 'K' Lanches Amigao y Restaurant (nombre comercial, si es distinto de la razón social)" />
                    <p className="text-slate-500 text-xs mt-1">Si se completa, aparece en la factura en grande, con la razón social y el RUC debajo.</p>
                  </div>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="text-slate-400 text-sm mb-1 block">Razón social</label>
                      <Input value={form.razon_social}
                        onChange={e => setForm(p => ({ ...p, razon_social: e.target.value }))}
                        className="bg-arandu-dark border-white/10 text-white"
                        placeholder="Ej: Mi Empresa S.A." />
                    </div>
                    <div>
                      <label className="text-slate-400 text-sm mb-1 block">RUC</label>
                      <Input value={form.ruc}
                        onChange={e => setForm(p => ({ ...p, ruc: e.target.value }))}
                        className="bg-arandu-dark border-white/10 text-white"
                        placeholder="Ej: 80000000-1" />
                    </div>
                  </div>
                  <div>
                    <label className="text-slate-400 text-sm mb-1 block">Dirección</label>
                    <Input value={form.direccion}
                      onChange={e => setForm(p => ({ ...p, direccion: e.target.value }))}
                      className="bg-arandu-dark border-white/10 text-white"
                      placeholder="Dirección que aparecerá en la factura" />
                  </div>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="text-slate-400 text-sm mb-1 block">Teléfono</label>
                      <Input value={form.telefono}
                        onChange={e => setForm(p => ({ ...p, telefono: e.target.value }))}
                        className="bg-arandu-dark border-white/10 text-white"
                        placeholder="Ej: 0981 000 000" />
                    </div>
                    <div>
                      <label className="text-slate-400 text-sm mb-1 block">Email</label>
                      <Input value={form.email}
                        onChange={e => setForm(p => ({ ...p, email: e.target.value }))}
                        className="bg-arandu-dark border-white/10 text-white"
                        placeholder="facturacion@empresa.com" />
                    </div>
                  </div>
                </div>

                {/* Números patronales */}
                <div className="bg-arandu-dark/60 border border-white/10 rounded-xl p-4 space-y-4">
                  <div>
                    <h3 className="text-slate-200 text-sm font-semibold">Registro laboral</h3>
                    <p className="text-slate-500 text-xs mt-0.5">Números patronales para reportes de aportes y nómina.</p>
                  </div>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="text-slate-400 text-sm mb-1 block">Nº Patronal IPS</label>
                      <Input value={form.nro_patronal_ips}
                        onChange={e => setForm(p => ({ ...p, nro_patronal_ips: e.target.value }))}
                        className="bg-arandu-dark border-white/10 text-white font-mono"
                        placeholder="Ej: 00-000-000" />
                    </div>
                    <div>
                      <label className="text-slate-400 text-sm mb-1 block">Nº Patronal MTESS</label>
                      <Input value={form.nro_patronal_mtess}
                        onChange={e => setForm(p => ({ ...p, nro_patronal_mtess: e.target.value }))}
                        className="bg-arandu-dark border-white/10 text-white font-mono"
                        placeholder="Ej: 00-000-000" />
                    </div>
                  </div>
                </div>

                {/* Rubros habilitados DENIT */}
                <div className="bg-arandu-dark/60 border border-white/10 rounded-xl p-4 space-y-2">
                  <div>
                    <h3 className="text-slate-200 text-sm font-semibold">Rubros habilitados DENIT</h3>
                    <p className="text-slate-500 text-xs mt-0.5">Se imprime en facturas y comprobantes como actividad económica del emisor.</p>
                  </div>
                  <textarea
                    rows={2}
                    value={form.rubros_dinet}
                    onChange={e => setForm(p => ({ ...p, rubros_dinet: e.target.value }))}
                    className="w-full bg-arandu-dark border border-white/10 rounded-md text-white text-sm px-3 py-2 placeholder:text-slate-600 resize-none"
                    placeholder="Ej: Servicios informáticos, desarrollo de software, consultoría"
                  />
                </div>

                {/* Color de acento */}
                <div>
                  <label className="text-slate-400 text-sm mb-1 block">Color de acento</label>
                  <div className="flex items-center gap-3">
                    <input type="color" value={form.color}
                      onChange={e => setForm(p => ({ ...p, color: e.target.value }))}
                      className="w-12 h-10 rounded-lg border border-white/10 bg-transparent cursor-pointer" />
                    <Input value={form.color}
                      onChange={e => setForm(p => ({ ...p, color: e.target.value }))}
                      className="bg-arandu-dark border-white/10 text-white font-mono w-36"
                      placeholder="#3b82f6" />
                    <p className="text-slate-500 text-xs">Se usa en listados y badges de empresa</p>
                  </div>
                </div>

                {/* Logo → remitir al gestor */}
                {editingId && (
                  <div className="bg-arandu-dark/60 border border-white/10 rounded-xl p-4 flex items-center justify-between gap-4">
                    <div>
                      <p className="text-slate-300 text-sm font-medium">Logos de la empresa</p>
                      <p className="text-slate-500 text-xs mt-0.5">
                        Subí, etiquetá y asigná logos para el panel y los documentos impresos.
                      </p>
                    </div>
                    <Button type="button"
                      onClick={() => {
                        const ep = empresas.find(e => e.id === editingId);
                        if (ep) { setLogoManagerEmpresa(ep); setShowLogoManager(true); }
                      }}
                      variant="outline"
                      className="border-white/10 text-slate-300 hover:text-white whitespace-nowrap flex-shrink-0">
                      <Image className="w-4 h-4 mr-2" />
                      Gestionar logos
                    </Button>
                  </div>
                )}

                {/* Agente de retención de IVA en compras (se guarda aparte) */}
                {editingId && (
                  <RetencionAgentePanel token={token} slug={form.slug}
                    canEdit={user?.role === "admin" || user?.role === "gerente" || hasPermission?.("retenciones.editar_config")} />
                )}

                {/* Selector de tema */}
                <div>
                  <label className="text-slate-400 text-sm mb-3 block flex items-center gap-2">
                    <Palette className="w-4 h-4" /> Tema de color del panel
                  </label>
                  <div className="flex flex-wrap gap-4 justify-start">
                    {TEMAS.map(tema => (
                      <TemaPreview key={tema.id} tema={tema}
                        selected={form.tema === tema.id}
                        onClick={() => setForm(p => ({ ...p, tema: tema.id }))} />
                    ))}
                  </div>
                  {/* Descripción del tema seleccionado */}
                  <div className="mt-3 p-3 bg-arandu-dark rounded-lg border border-white/5">
                    <p className="text-slate-400 text-xs">{temaInfo(form.tema).desc}</p>
                  </div>
                </div>

                {/* Monedas habilitadas */}
                <div className="bg-arandu-dark/60 border border-white/10 rounded-xl p-4 space-y-3">
                  <div>
                    <h3 className="text-slate-200 text-sm font-semibold">Monedas habilitadas</h3>
                    <p className="text-slate-500 text-xs mt-0.5">PYG (₲ Guaraníes) siempre está habilitado. Activá BRL si la empresa opera en Reales Brasileños.</p>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-arandu-dark/40 rounded-xl border border-white/5">
                    <div>
                      <p className="text-white text-sm font-medium">Real Brasileño (BRL R$)</p>
                      <p className="text-slate-500 text-xs">Habilita facturas, compras y cuentas en reales</p>
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        const current = form.monedas_habilitadas || ["PYG"];
                        if (current.includes("BRL")) {
                          setForm(f => ({ ...f, monedas_habilitadas: current.filter(m => m !== "BRL") }));
                        } else {
                          setForm(f => ({ ...f, monedas_habilitadas: [...current, "BRL"] }));
                        }
                      }}
                      className={`w-10 h-6 rounded-full transition-colors flex-shrink-0 flex items-center ${(form.monedas_habilitadas || []).includes("BRL") ? "bg-emerald-500" : "bg-slate-600"}`}
                    >
                      <div className={`w-4 h-4 bg-white rounded-full shadow transition-transform mx-1 ${(form.monedas_habilitadas || []).includes("BRL") ? "translate-x-4" : "translate-x-0"}`} />
                    </button>
                  </div>
                </div>

                {/* Módulos habilitados */}
                <div>
                  <div className="flex items-start justify-between gap-3 mb-3">
                    <div>
                      <label className="text-slate-300 text-sm font-semibold block">Módulos habilitados</label>
                      <p className="text-slate-500 text-xs mt-0.5">
                        Desactivar un módulo oculta menú, dashboard, accesos directos, reportes y opciones relacionadas para esta empresa.
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => setForm(p => ({
                        ...p,
                        modulos_habilitados: (p.modulos_habilitados || []).length === DEFAULT_EMPRESA_MODULOS.length
                          ? [...EMPRESA_MODULOS_OBLIGATORIOS]
                          : [...DEFAULT_EMPRESA_MODULOS],
                      }))}
                      className="text-xs text-arandu-blue hover:underline whitespace-nowrap"
                    >
                      {(form.modulos_habilitados || []).length === DEFAULT_EMPRESA_MODULOS.length ? "Quitar todos" : "Activar todos"}
                    </button>
                  </div>
                  <div className="grid md:grid-cols-2 gap-2">
                    {Object.entries(EMPRESA_MODULOS).map(([key, modulo]) => {
                      const active = (form.modulos_habilitados || []).includes(key);
                      const obligatorio = !!modulo.obligatorio;
                      const dependenciasFaltantes = active
                        ? (modulo.dependencias || []).filter(d => !(form.modulos_habilitados || []).includes(d))
                        : [];
                      return (
                        <button
                          key={key}
                          type="button"
                          onClick={() => toggleModulo(key)}
                          className={`text-left rounded-xl border px-3 py-3 transition-all ${
                            active
                              ? "border-emerald-500/40 bg-emerald-500/10"
                              : "border-white/10 bg-arandu-dark text-slate-500 hover:border-white/20"
                          }`}
                        >
                          <div className="flex items-start gap-2">
                            <span className={`mt-0.5 w-4 h-4 rounded border flex items-center justify-center flex-shrink-0 ${active ? "bg-emerald-500 border-emerald-500" : "border-white/20"}`}>
                              {active && <Check className="w-3 h-3 text-white" />}
                            </span>
                            <span>
                              <span className={`block text-sm font-medium ${active ? "text-emerald-200" : "text-slate-400"}`}>{modulo.label}</span>
                              {obligatorio && <span className="inline-block text-[10px] text-amber-300 border border-amber-500/30 bg-amber-500/10 rounded px-1.5 py-0.5 mt-1">Obligatorio</span>}
                              {dependenciasFaltantes.length > 0 && (
                                <span className="block text-[10px] text-red-300 border border-red-500/30 bg-red-500/10 rounded px-1.5 py-0.5 mt-1 w-fit">
                                  Falta habilitar: {dependenciasFaltantes.map(d => EMPRESA_MODULOS[d]?.label || d).join(", ")}
                                </span>
                              )}
                              <span className="block text-xs text-slate-500 mt-0.5">{modulo.desc}</span>
                              {modulo.permisos_minimos?.length > 0 && (
                                <span className={`block mt-2 rounded-lg border px-2 py-2 ${
                                  active
                                    ? "border-amber-400/30 bg-amber-400/10"
                                    : "border-white/10 bg-black/10"
                                }`}>
                                  <span className="block text-[10px] uppercase tracking-wide text-amber-300 font-semibold mb-1">
                                    Permisos mínimos sugeridos
                                  </span>
                                  <span className="block text-[11px] text-slate-300 font-mono leading-relaxed">
                                    {modulo.permisos_minimos.join(" · ")}
                                  </span>
                                  {modulo.permisos_recomendados?.length > 0 && (
                                    <span className="block text-[11px] text-slate-500 mt-1 leading-relaxed">
                                      Extras: {modulo.permisos_recomendados.join(" · ")}
                                    </span>
                                  )}
                                </span>
                              )}
                            </span>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div className="flex gap-3 pt-2">
                  <Button type="button" variant="outline" onClick={() => setShowForm(false)}
                    className="flex-1 border-white/10 text-slate-400">
                    <X className="w-4 h-4 mr-2" /> Cancelar
                  </Button>
                  <Button type="submit" disabled={saving}
                    className="flex-1 bg-arandu-blue hover:bg-arandu-blue-dark text-white">
                    <Save className="w-4 h-4 mr-2" />
                    {saving ? "Guardando..." : editingId ? "Actualizar" : "Crear empresa"}
                  </Button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ══ MODAL: Gestionar logos ═══════════════════════════════════════════════ */}
      <AnimatePresence>
        {showLogoManager && logoManagerEmpresa && (
          <LogoManagerModal
            key={logoManagerEmpresa.id}
            empresa={logoManagerEmpresa}
            token={token}
            onClose={() => { setShowLogoManager(false); setLogoManagerEmpresa(null); }}
            onSaved={() => { fetchEmpresas(); refreshEmpresasPropias(); }}
          />
        )}
      </AnimatePresence>

      {/* Input oculto para subir contrato */}
      <input
        ref={contratoFileRef}
        type="file"
        accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
        className="hidden"
        onChange={e => {
          const empresaId = contratoFileRef.current?.getAttribute("data-empresa-id");
          if (empresaId && e.target.files?.[0]) handleUploadContrato(empresaId, e.target.files[0]);
        }}
      />

      {/* Overlay para cerrar menú contrato */}
      {contratoMenuId && (
        <div className="fixed inset-0 z-10" onClick={() => setContratoMenuId(null)} />
      )}

      {/* ══ MODAL: Confirmar limpieza de base de datos ═══════════════════════════ */}
      <AnimatePresence>
        {showWipeModal && wipeTargetEmpresa && (
          <WipeConfirmModal
            key={wipeTargetEmpresa.id}
            empresa={wipeTargetEmpresa}
            confirmText={wipeConfirmText}
            setConfirmText={setWipeConfirmText}
            saving={wipeSaving}
            onClose={() => { setShowWipeModal(false); setWipeTargetEmpresa(null); setWipeConfirmText(""); }}
            onConfirm={handleWipeConfirm}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

export default EmpresasPropiasPage;
